/**
 * useAdminMessages Hook
 * Manages admin direct messages with a specific user
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { supabaseAdmin } from '../integrations/supabase/client'
import { attachmentPlaceholder, validateAttachment } from '../lib/messages/attachments.js'

interface AdminMessage {
  id: string
  admin_id: string
  user_id: string
  sender_id: string
  message_text: string
  attachment_url?: string
  attachment_type?: string
  attachment_name?: string
  attachment_size_bytes?: number
  is_read_admin?: boolean
  is_read_user?: boolean
  created_at: string
  sender: {
    id: string
    full_name: string
    avatar_url?: string
  }
  user: {
    id: string
    full_name: string
    email: string
    role: string
  }
}

export function useAdminMessages(userId: string | null) {
  const [messages, setMessages] = useState<AdminMessage[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isConnected, setIsConnected] = useState(false)
  const channelRef = useRef<any>(null)

  // Fetch messages
  const fetchMessages = useCallback(async () => {
    if (!userId) return

    try {
      setIsLoading(true)
      setError(null)

      const { data: { session } } = await supabaseAdmin.auth.getSession()
      if (!session) {
        throw new Error('Not authenticated')
      }

      const response = await fetch(`/api/admin-messages/user/${userId}`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || `Failed to fetch messages (${response.status})`)
      }

      const result = await response.json()
      setMessages(result.messages || [])
    } catch (err: any) {
      console.error('Error fetching admin messages:', err)
      setError(err.message)
    } finally {
      setIsLoading(false)
    }
  }, [userId])

  // Attach one file to a message already sent (multipart "file"); the row's attachment columns fill in
  // and the realtime UPDATE below merges them into the bubble.
  const uploadAttachment = useCallback(async (messageId: string, file: File, token: string) => {
    const form = new FormData()
    form.append('file', file)
    const response = await fetch(`/api/admin-messages/${messageId}/attachment`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: form
    })
    const result = await response.json().catch(() => ({}))
    if (!response.ok) throw new Error(result.message || 'Failed to upload attachment')
    return result
  }, [])

  // Send message, optionally with one attachment. A file sent without words gets placeholder text.
  const sendMessage = useCallback(async (messageText: string, file?: File | null) => {
    const text = (messageText || '').trim() || (file ? attachmentPlaceholder(file.name) : '')
    if (!userId || !text) {
      return { success: false, error: 'Message text is required' }
    }
    if (file) {
      const check = validateAttachment(file)
      if (!check.ok) return { success: false, error: check.error }
    }

    try {
      const { data: { session } } = await supabaseAdmin.auth.getSession()
      if (!session) {
        return { success: false, error: 'Not authenticated' }
      }

      const response = await fetch(`/api/admin-messages/user/${userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ message_text: text })
      })

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.message || 'Failed to send message')
      }

      if (file && result.message?.id) {
        try {
          const attachment = await uploadAttachment(result.message.id, file, session.access_token)
          return { success: true, message: { ...result.message, ...attachment } }
        } catch (uploadErr: any) {
          console.error('Error uploading admin message attachment:', uploadErr)
          return { success: false, error: `Message sent, but the file did not upload: ${uploadErr.message}` }
        }
      }

      return { success: true, message: result.message }
    } catch (err: any) {
      console.error('Error sending admin message:', err)
      return { success: false, error: err.message }
    }
  }, [userId, uploadAttachment])

  // Mark as read
  const markAsRead = useCallback(async (messageId: string) => {
    try {
      const { data: { session } } = await supabaseAdmin.auth.getSession()
      if (!session) return

      await fetch(`/api/admin-messages/${messageId}/read`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      })

      return { success: true }
    } catch (err: any) {
      console.error('Error marking message as read:', err)
      return { success: false, error: err.message }
    }
  }, [])

  // Set up real-time subscription
  useEffect(() => {
    if (!userId) return

    fetchMessages()

    // Subscribe to admin_messages changes for this user
    const channel = supabaseAdmin
      .channel(`admin_messages:${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'admin_messages',
          filter: `user_id=eq.${userId}`
        },
        async (payload) => {
          // Fetch sender info
          const { data: sender } = await supabaseAdmin
            .from('users')
            .select('id, full_name, avatar_url')
            .eq('id', payload.new.sender_id)
            .single()

          const { data: user } = await supabaseAdmin
            .from('users')
            .select('id, full_name, email, role')
            .eq('id', payload.new.user_id)
            .single()

          const newMessage: AdminMessage = {
            ...payload.new,
            sender,
            user
          } as AdminMessage

          setMessages(prev => {
            if (prev.some(msg => msg.id === newMessage.id)) {
              return prev
            }
            return [...prev, newMessage]
          })
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'admin_messages',
          filter: `user_id=eq.${userId}`
        },
        (payload) => {
          setMessages(prev =>
            prev.map(msg =>
              msg.id === payload.new.id
                ? { ...msg, ...payload.new }
                : msg
            )
          )
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          setIsConnected(true)
          console.log(`✅ Subscribed to admin messages for user ${userId}`)
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          setIsConnected(false)
          console.error(`❌ Admin messages subscription error for user ${userId}:`, status)
        }
      })

    channelRef.current = channel

    return () => {
      if (channelRef.current) {
        supabaseAdmin.removeChannel(channelRef.current)
        console.log(`🔌 Unsubscribed from admin messages for user ${userId}`)
      }
    }
  }, [userId, fetchMessages])

  return {
    messages,
    isLoading,
    error,
    isConnected,
    sendMessage,
    markAsRead,
    refetch: fetchMessages
  }
}
